'use client'

import {
  FieldValues,
  UseControllerProps,
  useController,
  useFormContext,
} from 'react-hook-form'
import {
  Input as InputComponent,
  Label as LabelComponent,
} from '@material-bank/mb3-components'

export type InputProps<T extends FieldValues = FieldValues> = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'name' | 'defaultValue'
> &
  Omit<UseControllerProps<T>, 'control'> & {
    label?: string
    error?: string
    helperText?: string
    control?: UseControllerProps<T>['control']
    ref?: React.Ref<HTMLInputElement>
  }

export function Input({
  label,
  error,
  helperText,
  control: controlProp,
  name,
  rules,
  shouldUnregister,
  defaultValue,
  className = '',
  ref,
  ...inputProps
}: InputProps) {
  const formContext = useFormContext()
  const control = controlProp || formContext?.control

  if (!control) {
    throw new Error(
      'Input must be used inside a Form component or have control prop passed',
    )
  }

  const { field, fieldState } = useController({
    control,
    name: name as string,
    rules,
    shouldUnregister,
    defaultValue,
  })

  const errorMessage = error || fieldState.error?.message
  const hasError = !!errorMessage

  return (
    <div className="flex flex-col gap-4">
      {label && (
        <LabelComponent htmlFor={name} className="form-label">
          {label}
        </LabelComponent>
      )}
      <InputComponent
        id={name}
        {...field}
        value={field.value ?? ''}
        {...inputProps}
        ref={ref}
        variant={hasError ? 'destructive' : 'default'}
        className={className}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${name}-error` : undefined}
      />
    </div>
  )
}

Input.displayName = 'Input'
